import { useLingui } from '@lingui/react/macro'
import type { RegisterTabLeaveGuard } from 'features/knowledgeDocumentViewer/hooks/useTabLeaveGuards'
import type { TabHistoryItem } from 'features/knowledgeDocumentViewer/types'
import { useWorkspaceLeaveGuard } from 'features/workspaceTabs/hooks/useWorkspaceLeaveGuard'
import { useEffect } from 'react'

import type { LotoLeavePrompt } from './useLotoLeavePrompt'

type UseLotoLeaveGuardsArgs = {
  tabId: string
  activeReportId: string | undefined
  prompt: LotoLeavePrompt
  registerTabLeaveGuard: RegisterTabLeaveGuard
}

function keepsLoto(
  destination: TabHistoryItem | undefined,
  activeReportId: string | undefined
): boolean {
  return (
    activeReportId !== undefined &&
    destination?.type === 'document' &&
    destination.lotoPanel?.activeReportId === activeReportId
  )
}

export function useLotoLeaveGuards({
  tabId,
  activeReportId,
  prompt: { requestLeave, isDirty },
  registerTabLeaveGuard,
}: UseLotoLeaveGuardsArgs): void {
  const { t } = useLingui()
  useEffect(
    () =>
      registerTabLeaveGuard(tabId, (leave, destination) =>
        keepsLoto(destination, activeReportId) ? leave() : requestLeave(leave)
      ),
    [activeReportId, registerTabLeaveGuard, requestLeave, tabId]
  )
  useWorkspaceLeaveGuard({
    message: t`This LOTO has unsaved changes. Leave without saving them?`,
    isDirty,
  })
}
